import { useCallback, useEffect, useRef, useState } from "react";
import ThermalPrinter from "../utils/thermalPrinter";

const PRINTED_ORDERS_KEY = "qsr_printed_orders";
const PRINTER_SETTINGS_KEY = "qsr_printer_settings";
const MAX_PRINTED_IDS = 500;

const loadPrintedIDs = () => {
  try {
    return new Set(
      JSON.parse(localStorage.getItem(PRINTED_ORDERS_KEY) || "[]")
    );
  } catch {
    return new Set();
  }
};

const savePrintedIDs = (idSet) => {
  const arr = [...idSet];
  const trimmed =
    arr.length > MAX_PRINTED_IDS ? arr.slice(-MAX_PRINTED_IDS) : arr;
  localStorage.setItem(PRINTED_ORDERS_KEY, JSON.stringify(trimmed));
};

const loadSettings = () => {
  try {
    return JSON.parse(localStorage.getItem(PRINTER_SETTINGS_KEY) || "{}");
  } catch {
    return {};
  }
};

const useThermalPrinter = () => {
  const printerRef = useRef(null);
  const printedIDsRef = useRef(loadPrintedIDs());
  const printQueueRef = useRef([]);
  const isPrintingRef = useRef(false);
  const autoPrintRef = useRef(loadSettings().autoPrint !== false);

  const [connected, setConnected] = useState(false);
  const [autoPrintEnabled, _setAutoPrintEnabled] = useState(
    () => loadSettings().autoPrint !== false
  );
  const [lastError, setLastError] = useState(null);

  // Keep ref in sync + persist
  const setAutoPrintEnabled = useCallback((val) => {
    const next = typeof val === "function" ? val(autoPrintRef.current) : val;
    autoPrintRef.current = next;
    _setAutoPrintEnabled(next);
    const settings = loadSettings();
    settings.autoPrint = next;
    localStorage.setItem(PRINTER_SETTINGS_KEY, JSON.stringify(settings));
  }, []);

  // Create printer instance
  useEffect(() => {
    printerRef.current = new ThermalPrinter({ lineWidth: 30 });
    return () => {
      printerRef.current?.disconnect().catch(() => {});
    };
  }, []);

  // Sync connected state when BT disconnects
  useEffect(() => {
    const interval = setInterval(() => {
      const printer = printerRef.current;
      if (printer && connected && !printer.isConnected) {
        setConnected(false);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [connected]);

  /** Connect — user click required for Bluetooth pairing */
  const connect = useCallback(async () => {
    try {
      setLastError(null);
      const printer = printerRef.current;
      if (printer.isConnected) {
        await printer.disconnect();
      }
      await printer.connect();
      setConnected(true);
    } catch (err) {
      console.error("[Printer] Connect failed:", err);
      setLastError(err.message);
      setConnected(false);
    }
  }, []);

  /** Fallback connect that shows ALL Bluetooth devices */
  const connectAny = useCallback(async () => {
    try {
      setLastError(null);
      const printer = printerRef.current;
      if (printer.isConnected) {
        await printer.disconnect();
      }
      await printer.connectAny();
      setConnected(true);
    } catch (err) {
      console.error("[Printer] Connect failed:", err);
      setLastError(err.message);
      setConnected(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await printerRef.current?.disconnect();
    } catch (e) {
      // ignore
    }
    setConnected(false);
  }, []);

  const testPrint = useCallback(async () => {
    try {
      setLastError(null);
      await printerRef.current.testPrint();
    } catch (err) {
      console.error("[Printer] Test print failed:", err);
      setLastError("Test failed: " + err.message);
    }
  }, []);

  /** Process queue sequentially */
  const processQueue = useCallback(async () => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;

    while (printQueueRef.current.length > 0) {
      const order = printQueueRef.current.shift();
      try {
        await printerRef.current.printOrder(order);
        printedIDsRef.current.add(order.orderID);
        savePrintedIDs(printedIDsRef.current);
      } catch (err) {
        console.error("[Printer] Print failed:", err);
        setLastError("Print failed: " + err.message);
      }
    }

    isPrintingRef.current = false;
  }, []);

  /** Manual print */
  const printOrder = useCallback(
    (order) => {
      if (!printerRef.current?.isConnected) {
        setLastError("Printer not connected");
        return;
      }
      setLastError(null);
      printQueueRef.current.push(order);
      processQueue();
    },
    [processQueue]
  );

  /** Auto-print from WebSocket — uses refs only, never stale */
  const autoPrint = useCallback(
    (order) => {
      console.log("[Printer] autoPrint called",
        "orderID:", order?.orderID,
        "autoPrintEnabled:", autoPrintRef.current,
        "isConnected:", printerRef.current?.isConnected,
        "status:", order?.status,
        "alreadyPrinted:", printedIDsRef.current.has(order?.orderID));

      if (!order?.orderID) return;
      if (!autoPrintRef.current) return;
      if (!printerRef.current?.isConnected) return;
      if (printedIDsRef.current.has(order.orderID)) return;

      console.log("[Printer] Auto-printing order", order.orderNumber || order.orderID);
      printQueueRef.current.push(order);
      processQueue();
    },
    [processQueue]
  );

  const hasPrinted = useCallback((orderID) => {
    return printedIDsRef.current.has(orderID);
  }, []);

  return {
    connected,
    autoPrintEnabled,
    setAutoPrintEnabled,
    lastError,
    connect,
    connectAny,
    disconnect,
    testPrint,
    printOrder,
    autoPrint,
    hasPrinted,
  };
};

export default useThermalPrinter;
